import { corsTextureUrl } from '../cardImageUrl';

// Derives a single accent colour from a profile's background art so
// the profile's panel strokes and corner plating can pick up that
// player's colour signature.
//
// This is deliberately NOT "the most common colour". Card art is
// mostly dark, desaturated ground (brown stone, murky water, night
// sky) with one or two vivid features on top (a light blue glow, a
// red banner). The most common colour would turn every profile a
// muddy brown. What reads as the art's accent is the colour that
// POPS, so the pick is driven by vividness first and coverage only
// as a weak, sub-linear tiebreaker with a floor:
//
//   1. Only colourful pixels vote. Anything below MIN_CHROMA (greys,
//      near-black shadow, dull brown) is ignored outright, and votes
//      are further weighted towards mid lightness so deep navy
//      shadows and blown-out highlights don't dominate.
//   2. Votes are bucketed by hue. Each bucket's score is
//      vividness^2 * coverage^0.3, so a vivid 6% feature beats a
//      dull 40% one, but stray single pixels can't win because a
//      bucket also needs MIN_COVERAGE of the whole image.
//   3. The winning hue is re-emitted at a clamped saturation and
//      lightness band close to the theme's GOLD stroke, so the
//      resulting stroke is always readable and never a pastel or a
//      shadow tone, whatever the source image looked like.
//
// Returns null when nothing colourful clears the floor (grayscale or
// near-monochrome art), so callers fall back to the gold default.

const SAMPLE_SIZE = 48;
const HUE_BINS = 24;
const BIN_DEGREES = 360 / HUE_BINS;
// Fraction of ALL sampled pixels a 3-bin hue window must cover to
// be eligible. Keeps a handful of stray sparkle pixels from winning.
const MIN_COVERAGE = 0.02;
// HSL chroma (max - min, 0..1) below which a pixel is "not a colour"
// and does not vote.
const MIN_CHROMA = 0.18;
const COVERAGE_EXPONENT = 0.3;

const OUT_SAT_MIN = 0.5;
const OUT_SAT_MAX = 0.8;
const OUT_LIGHT_MIN = 0.5;
const OUT_LIGHT_MAX = 0.62;

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

// Triangular window peaking at mid lightness, zero at the extremes.
function lightnessWeight(l) {
  return clamp(1 - Math.abs(l - 0.55) / 0.45, 0, 1);
}

function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = l - c / 2;
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

/**
 * Pick an accent colour from raw RGBA pixel data.
 *
 * @param {Uint8ClampedArray|number[]} data RGBA bytes, 4 per pixel.
 * @returns {{ r: number, g: number, b: number } | null}
 */
export function pickAccentColor(data) {
  const total = Math.floor(data.length / 4);
  if (total === 0) return null;

  const bins = Array.from({ length: HUE_BINS }, () => ({
    count: 0, vivid: 0, sat: 0, light: 0, sin: 0, cos: 0,
  }));

  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue;
    const r = data[i] / 255;
    const g = data[i + 1] / 255;
    const b = data[i + 2] / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const chroma = max - min;
    const l = (max + min) / 2;
    if (chroma < MIN_CHROMA || l < 0.12 || l > 0.92) continue;

    const s = chroma / (1 - Math.abs(2 * l - 1));
    let h;
    if (max === r) h = ((g - b) / chroma) % 6;
    else if (max === g) h = (b - r) / chroma + 2;
    else h = (r - g) / chroma + 4;
    h = ((h * 60) + 360) % 360;

    const v = s * lightnessWeight(l);
    const bin = bins[Math.floor(h / BIN_DEGREES) % HUE_BINS];
    const rad = (h * Math.PI) / 180;
    bin.count += 1;
    bin.vivid += v;
    bin.sat += s;
    bin.light += l;
    bin.sin += Math.sin(rad) * v;
    bin.cos += Math.cos(rad) * v;
  }

  // Score a sliding 3-bin window so a hue straddling a bin edge
  // isn't split into two losing halves.
  let best = null;
  for (let i = 0; i < HUE_BINS; i += 1) {
    const window = [bins[(i + HUE_BINS - 1) % HUE_BINS], bins[i], bins[(i + 1) % HUE_BINS]];
    const count = window.reduce((n, w) => n + w.count, 0);
    if (count === 0) continue;
    const coverage = count / total;
    if (coverage < MIN_COVERAGE) continue;
    const vivid = window.reduce((n, w) => n + w.vivid, 0) / count;
    const score = vivid * vivid * Math.pow(coverage, COVERAGE_EXPONENT);
    if (!best || score > best.score) best = { score, window, count };
  }
  if (!best) return null;

  const sum = (key) => best.window.reduce((n, w) => n + w[key], 0);
  const hue = (Math.atan2(sum('sin'), sum('cos')) * 180) / Math.PI;
  const sat = clamp(sum('sat') / best.count, OUT_SAT_MIN, OUT_SAT_MAX);
  const light = clamp(sum('light') / best.count, OUT_LIGHT_MIN, OUT_LIGHT_MAX);
  return hslToRgb(hue, sat, light);
}

const cache = new Map();

/**
 * Load an image and derive its accent colour. Resolves to null on any
 * failure (network, tainted canvas, monochrome art). Results are
 * cached per URL for the session.
 *
 * @param {string} url
 * @returns {Promise<{ r: number, g: number, b: number } | null>}
 */
export function extractAccentFromImage(url) {
  if (!url || typeof Image === 'undefined') return Promise.resolve(null);
  if (cache.has(url)) return cache.get(url);
  const promise = new Promise((resolve) => {
    const img = new Image();
    // Same-origin on desktop, CORS-enabled CDN on web. Without the
    // flag the canvas is tainted and getImageData throws.
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = SAMPLE_SIZE;
        canvas.height = SAMPLE_SIZE;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        resolve(pickAccentColor(ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = corsTextureUrl(url);
  });
  cache.set(url, promise);
  return promise;
}

// The overlay needs more than one shade: strokes, headline text, a
// highlight, a shade and a deep shade (see ACCENT_FALLBACK in
// medievalTheme.jsx for the gold originals). All are the picked hue
// at fixed saturation / lightness offsets that mirror the spacing of
// the gold ramp, so the page keeps its tonal hierarchy under any hue.
function rgbToHsl({ r, g, b }) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const c = max - min;
  if (c === 0) return { h: 0, s: 0, l };
  const s = c / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === rn) h = ((gn - bn) / c) % 6;
  else if (max === gn) h = (bn - rn) / c + 2;
  else h = (rn - gn) / c + 4;
  return { h: (h * 60 + 360) % 360, s, l };
}

const triple = (h, s, l) => {
  const { r, g, b } = hslToRgb(h, s, l);
  return `${r}, ${g}, ${b}`;
};

/**
 * Expand an accent colour into the `--vh-accent-*` custom properties the
 * theme's ACCENT_* tokens read.
 *
 * @param {{ r: number, g: number, b: number }} color
 * @returns {Record<string, string>}
 */
export function accentPalette(color) {
  const { h, s, l } = rgbToHsl(color);
  return {
    '--vh-accent': triple(h, s, l),
    '--vh-accent-bright': triple(h, Math.min(s + 0.13, 0.9), Math.min(l + 0.08, 0.7)),
    '--vh-accent-hi': triple(h, 0.9, 0.85),
    '--vh-accent-lo': triple(h, s, 0.48),
    '--vh-accent-deep': triple(h, Math.min(s + 0.1, 0.9), 0.34),
  };
}
